import {
  useState,
  KeyboardEvent,
  useCallback,
  forwardRef,
  useMemo,
} from 'react';
import { Button, InputAdornment, Stack } from '@mui/material';

import { Icon, useDrawerFilter } from '@hapvida/hapvida-core-components';

import newUseFilterParams from '@hapvida/hapvida-core-components/src/hooks/newUseFilterParams';

import { type SearchFieldProps, KeyboardEvents } from './types';
import { StyledTextField } from './styles';

const NewSearchField = forwardRef<HTMLDivElement, SearchFieldProps>(
  (
    {
      placeholder,
      currentResultLength,
      disableSearch = false,
      disableGutters,
      name,
      typingSearch = false,
    },
    ref,
  ) => {
    const { filterCount } = useDrawerFilter();

    const { params, changeFilter } = newUseFilterParams({
      filters: {
        [name]: '',
      },
    });

    const [searchState, setSearchState] = useState<string>(params[name] ?? '');

    const isResultEmpty = currentResultLength === 0;
    const hasFilterCount = filterCount > 0;
    const isSearchEmpty = !params[name];

    const minSearchValue = typingSearch ? 0 : 5;

    const isErroMinSearch = Boolean(
      searchState?.length && searchState?.length < minSearchValue,
    );

    const disableButtonSearch =
      (isResultEmpty && hasFilterCount && isSearchEmpty) ||
      disableSearch ||
      isErroMinSearch;

    const handleSubmitForm = useCallback(() => {
      if (isErroMinSearch) return;
      if (params[name] === searchState) {
        return;
      }

      changeFilter(name, searchState);
    }, [searchState, params[name], isErroMinSearch]);

    const handlePressEnter = useCallback(
      ({ key }: KeyboardEvent) => {
        const isEnter = key === KeyboardEvents.ENTER;
        if (isEnter) {
          handleSubmitForm();
        }
      },
      [KeyboardEvents, handleSubmitForm],
    );

    const handleQuerySelectorClick = () => {
      if (ref && typeof ref !== 'function') {
        ref.current?.querySelector('input')?.focus();
      }
    };

    const handleClickMagnifier = useCallback(() => {
      handleQuerySelectorClick();
    }, [ref]);

    const handleClickClose = useCallback(() => {
      changeFilter(name, '');
      setSearchState('');
      handleQuerySelectorClick();
    }, [ref]);

    const handleError = isErroMinSearch ? 'Digite no mínimo 5 caracteres' : '';

    useMemo(() => {
      setSearchState(params[name]);
    }, [params[name]]);

    const handleOnChange = useCallback(
      (value: string) => {
        setSearchState(value);
        if (typingSearch) {
          changeFilter(name, value);
        }
      },
      [typingSearch, changeFilter, name],
    );

    return (
      <Stack spacing={disableGutters ? 0 : 1} direction="row">
        <StyledTextField
          ref={ref}
          error={isErroMinSearch}
          helperText={handleError}
          onKeyDown={handlePressEnter}
          placeholder={placeholder}
          onChange={e => handleOnChange(e.target.value)}
          fullWidth
          variant="outlined"
          value={searchState}
          disableGutters={disableGutters}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon
                  onClick={handleClickMagnifier}
                  name="actions-b2b-search"
                  size={24}
                  style={{ cursor: 'text' }}
                />
              </InputAdornment>
            ),
            endAdornment:
              searchState?.length > 0 ? (
                <InputAdornment position="end">
                  <Icon
                    onClick={handleClickClose}
                    name="actions-b2b-close"
                    size={24}
                  />
                </InputAdornment>
              ) : null,
          }}
        />
        {!typingSearch && (
          <Button
            variant="contained"
            onClick={handleSubmitForm}
            color="secondary"
            disableRipple
            disabled={disableButtonSearch}
          >
            Buscar
          </Button>
        )}
      </Stack>
    );
  },
);

NewSearchField.displayName = 'NewSearchField';

export default NewSearchField;
